import { SOURCE, PUBLIC_PROTOCOL_VERSION } from './protocol'

// v1 message names (what callers pass to sendPublic) mapped to the
// legacy v0 wire shape: `key` is the envelope field that carries the
// type, and `legacyType` is the historical string we still emit on
// that field. v0 is the stringified-JSON shape from before the FSA
// envelope existed; we keep emitting it for any partner that hasn't
// opted into v1 via `data-message-version="1"`. The v1 wire uses the
// new key names (donationCompleted, fundraiserUpdated, …); v0 keeps
// the original DonateCompleted, updateEvent, … strings partners are
// already parsing.
//
// New message types added after the v0 freeze do not appear here —
// sendPublic emits v1 only for them. That's intentional: there is no
// legacy partner contract to honor for a message that never existed
// in v0.
const V0_SHAPES = {
  donationCompleted:        { key: 'message', legacyType: 'DonateCompleted'   },
  fundraiserUpdated:        { key: 'action',  legacyType: 'updateEvent'       },
  fundraiserRemoved:        { key: 'action',  legacyType: 'removeEvent'       },
  donateClicked:            { key: 'action',  legacyType: 'donate'            },
  changeBeneficiaryClicked: { key: 'action',  legacyType: 'changeBeneficiary' },
}

// Stamped on every v1 envelope while PUBLIC_PROTOCOL_VERSION is in
// beta. Promote to documentation + drop this constant when v1 is
// published.
const BETA_NOTICE =
  'INTERNAL USE ONLY — the v1 partner-facing message protocol is in '
  + 'beta and may change without notice until it is published.'

// Versions sendPublic knows how to emit. Pin values outside this set
// (typos like data-message-version="2", or `message_version=abc`)
// silently drop both wire forms today — we warn once at parse time
// so the misconfiguration surfaces in the partner's console.
const KNOWN_VERSIONS = new Set([ 1 ])

// Pins we have already warned about during this page's lifetime —
// keyed on the raw URL value so each distinct typo earns its own
// warning (e.g. after a navigation), but a single bad pin doesn't
// spam the console on every send.
const warnedPins = new Set()

// Resolved at call time so iframes that navigate (path 3 of S&D, for
// example) pick up the new URL's pin in the post-navigation context.
const readPinnedVersion = () => {
  const raw = new URLSearchParams(location.search).get('message_version')
  if (raw === null || raw === '') return undefined
  const parsed = parseInt(raw, 10)
  if (KNOWN_VERSIONS.has(parsed)) return parsed
  if (!warnedPins.has(raw)) {
    warnedPins.add(raw)
    console.warn(
      `publicProtocol: unrecognized message_version=${JSON.stringify(raw)} in `
      + 'the URL. Outbound public messages will be dropped. Expected one of: '
      + [ ...KNOWN_VERSIONS ].join(', ')
    )
  }
  return parsed
}

// True when the partner has not pinned a version via
// `data-message-version`. Callers use this to gate any pre-v0
// legacy plain-string broadcasts (notifyParent's `npoSelected ID`,
// `eventGoal N`, `npoRemoved`) that predate even the documented v0
// contract — partners on v1 explicitly opted out of legacy noise.
export const unpinned = () => readPinnedVersion() === undefined

// Send a partner-facing message. Wire format depends on the
// partner's `data-message-version` opt-in:
//   - unspecified → parallel-send v0 (legacy stringified) and v1 (FSA broadcast)
//   - 1           → v1 only
//
// Types that exist in V0_SHAPES get the parallel v0 send; types
// added after the v0 freeze are v1-only regardless of the pin (no
// legacy contract to honor). For internal-only broadcast traffic
// that should not be in the partner protocol at all, use
// sendBroadcast from broadcast.js instead.
export const sendPublic = (target, type, payload) => {
  const pinnedVersion = readPinnedVersion()
  const v0Shape = V0_SHAPES[type]

  if (pinnedVersion === undefined && v0Shape) {
    const { key, legacyType } = v0Shape
    target.postMessage(JSON.stringify({ [key]: legacyType, data: payload }), '*')
  }
  if (pinnedVersion === undefined || pinnedVersion === 1) {
    target.postMessage({
      type,
      payload,
      meta: {
        source: SOURCE,
        version: PUBLIC_PROTOCOL_VERSION,
        notice: BETA_NOTICE,
      },
    }, '*')
  }
}
